import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatNewsTime } from './community/utils';
import './NewsInsight.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://community.ilouli.com';

// 아이콘 컴포넌트
const Icon = ({ name, size = 16, className = '' }) => {
  const icons = {
    externalLink: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
    ),
    news: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
        <path d="M18 14h-8"/>
        <path d="M15 18h-5"/>
        <path d="M10 6h8v4h-8V6Z"/>
      </svg>
    ),
    warning: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  };
  return <span className={className}>{icons[name] || null}</span>;
};

const NewsInsight = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const ITEMS_PER_PAGE = 20;

  // 카테고리 로드
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/news/insight/categories`);
        const data = await response.json();
        if (data.success) {
          setCategories(data.categories);
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // 기사 로드
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }

      const response = await fetch(`${API_BASE_URL}/api/news/insight/latest?${params}`);
      const data = await response.json();

      if (data.success) {
        setArticles(data.articles || []);
        setStats(data.stats);
      } else {
        setError(data.error || '기사를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('기사를 불러오는데 실패했습니다.');
      console.error('Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // 검색 필터링
  const filteredArticles = useMemo(() => {
    if (!searchQuery) return articles;
    const query = searchQuery.toLowerCase();
    return articles.filter(article =>
      article.title?.toLowerCase().includes(query) ||
      article.description?.toLowerCase().includes(query) ||
      article.content?.toLowerCase().includes(query)
    );
  }, [articles, searchQuery]);

  // 페이지네이션
  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const paginatedArticles = filteredArticles.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // 검색 실행
  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPage(1);
  };

  // 검색 초기화
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  // 기사 펼치기/접기
  const toggleArticle = (guid) => {
    setExpandedArticle(expandedArticle === guid ? null : guid);
  };

  // formatDate: community/utils.js에서 import
  const formatDate = formatNewsTime;

  // 카테고리 라벨
  const getCategoryLabel = useMemo(() => {
    const labelMap = {};
    categories.forEach(cat => {
      labelMap[cat.code] = cat.label;
    });
    return labelMap;
  }, [categories]);

  return (
    <div className="news-page">
      <div className="news-header">
        <div className="header-content">
          <h1>뉴스 인사이트</h1>
          <p className="subtitle">국내 주요 언론사 RSS 직접 수집 · {stats?.total || 0}개 기사</p>
        </div>
      </div>

      {/* 검색 섹션 */}
      <div className="news-search-section">
        <h3>뉴스 검색</h3>
        <div className="search-input-row">
          <input
            type="text"
            placeholder="검색어 입력... (제목, 내용)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>검색</button>
        </div>
        {searchQuery && (
          <div className="search-result-info">
            <span className="search-query-tag">
              "{searchQuery}"
              <button onClick={handleClearSearch}>×</button>
            </span>
            <span className="search-result-text">{filteredArticles.length}개 결과</span>
          </div>
        )}
      </div>

      {/* 카테고리 필터 */}
      <div className="news-filters">
        <div className="category-tabs">
          <button
            className={`category-tab ${selectedCategory === '' ? 'active' : ''}`}
            onClick={() => { setSelectedCategory(''); setPage(1); }}
          >
            전체
          </button>
          {categories.map(cat => (
            <button
              key={cat.code}
              className={`category-tab ${selectedCategory === cat.code ? 'active' : ''}`}
              onClick={() => { setSelectedCategory(cat.code); setPage(1); }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* 뉴스 목록 */}
      <div className="news-content">
        {loading ? (
          <div className="news-loading">
            <div className="spinner"></div>
            <p>뉴스를 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="news-error">
            <Icon name="warning" size={48} className="error-icon" />
            <p>{error}</p>
            <button onClick={fetchArticles}>다시 시도</button>
          </div>
        ) : paginatedArticles.length === 0 ? (
          <div className="news-empty">
            <Icon name="news" size={48} className="empty-icon" />
            <p>뉴스가 없습니다.</p>
          </div>
        ) : (
          <div className="news-list">
            {paginatedArticles.map((article, idx) => {
              const isExpanded = expandedArticle === article.guid;
              const hasContent = article.content && article.content.length > 0;

              return (
                <article key={article.guid || idx} className="news-item">
                  <div className="news-item-main">
                    <div className="news-item-header">
                      <span className="news-category">
                        {getCategoryLabel[article.category] || article.categoryLabel || article.category}
                      </span>
                      <span className="news-source">{article.source}</span>
                      <span className="news-time">{formatDate(article.pubDate)}</span>
                      {hasContent && (
                        <span className="news-content-badge">본문</span>
                      )}
                    </div>
                    <h3 className="news-title">
                      <a href={article.link} target="_blank" rel="noopener noreferrer">
                        {article.title}
                      </a>
                    </h3>

                    {/* 요약 또는 본문 */}
                    {isExpanded && hasContent ? (
                      <div className="news-full-content">
                        <p>{article.content}</p>
                      </div>
                    ) : (
                      <p className="news-description">
                        {article.description?.length > 150
                          ? article.description.substring(0, 150) + '...'
                          : article.description}
                      </p>
                    )}
                  </div>

                  <div className="news-item-actions">
                    {hasContent && (
                      <button
                        className={`content-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleArticle(article.guid)}
                        title={isExpanded ? '접기' : '본문 보기'}
                      >
                        {isExpanded ? '접기' : '본문'}
                      </button>
                    )}
                    <a
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-link"
                      title="원문 보기"
                    >
                      <Icon name="externalLink" size={16} />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="news-pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              이전
            </button>
            <span className="page-info">{page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsInsight;
